import { carriesCredential } from "./sanitize";

/**
 * SENSITIVE_INPUT_TYPES are the input types whose value is masked in every
 * session replay. Everything else stays legible: a replay of the studio is only
 * useful if the search box, the itinerary name, and the numeric filters read as
 * what the user actually typed.
 */
const SENSITIVE_INPUT_TYPES = new Set(["email", "password"]);

/**
 * SENSITIVE_FIELD_PATTERN matches the field identifiers that carry an address or
 * a credential regardless of the rendered input type. Ory builds its self-service
 * forms from flow nodes and only guarantees `type="password"` for the password
 * field — the identifier on the login form renders as a plain text input — so the
 * type check alone would leak the sign-in address into the replay. The one-time code
 * of a recovery or a verification is a text input named `code`, autocompleted as
 * `one-time-code`: `code` is matched as a whole word, so a field whose name merely
 * contains it (`promotion_code`) stays legible.
 */
const SENSITIVE_FIELD_PATTERN = /(email|password|identifier|username|\bcode\b)/i;

/**
 * maskSensitiveInput is the session-replay input mask. It reveals an input's
 * value unless the field is an email address, a password, or a sign-in
 * identifier, or unless the value itself carries a credential parameter, in
 * which case the value is replaced with a same-length run of asterisks so the
 * replay keeps the field's shape without its content.
 *
 * The value is read as well as the field because a credential can sit in a field
 * no name gives away: the share sheet's link field is a plain read-only text input,
 * and its value is the share link, token included.
 *
 * PostHog only routes an input through this function when `maskAllInputs` is
 * true, so config.ts pairs the two: the flag opts every input in, and this
 * function is what opts the harmless ones back out.
 *
 * It fails closed. rrweb types the element as always present, but a masker that
 * silently reveals a value it could not classify is the wrong failure, so a
 * missing element masks.
 */
export const maskSensitiveInput = (text: string, element?: HTMLElement | null): string => {
  if (!element || carriesCredential(text)) {
    return maskValue(text);
  }

  const type = element.getAttribute("type")?.toLowerCase() ?? "";
  if (SENSITIVE_INPUT_TYPES.has(type)) {
    return maskValue(text);
  }

  // name / id / autocomplete are checked together: a field is sensitive if ANY of
  // them names it so, and joining them into one haystack keeps that a single test.
  const identifiers = [
    element.getAttribute("name"),
    element.getAttribute("id"),
    element.getAttribute("autocomplete"),
  ].join(" ");

  if (SENSITIVE_FIELD_PATTERN.test(identifiers)) {
    return maskValue(text);
  }

  return text;
};

/**
 * maskValue replaces a value with a same-length run of asterisks, so the replay
 * preserves how much was typed without revealing what.
 */
const maskValue = (text: string): string => "*".repeat(text.length);
